import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import {
  Icon as PhosphorIcon,
  IconProps as PhosphorIconProps,
} from "@phosphor-icons/react";
import { Icon, iconVariants } from "./icon";

import { cn } from "../../lib/utils";

const buttonVariants = cva(
  "ui-flex ui-justify-center ui-items-center ui-transition-all ui-duration-200",
  {
    variants: {
      variant: {
        primary:
          "ui-z-1 ui-relative ui-bg-gradient-to-b ui-from-indigo-500/80 ui-to-indigo-500 ui-bg-white  shadow-surface-bright \
          hover:ui-bg-black/10 ui-text-white \
          focus-visible:ui-outline-none focus-visible:ui-ring-1 focus-visible:ui-ring-indigo-500/50 focus-visible:ui-ring-offset-1 focus-visible:ui-ring-offset-transparent \
          disabled:ui-opacity-40 disabled:ui-cursor-not-allowed \
          active:ui-bg-gradient-to-b active:ui-from-indigo-600/80 active:ui-to-indigo-600 \
          data-[active=true]:ui-bg-gradient-to-b data-[active=true]:ui-from-indigo-600/80 data-[active=true]:ui-to-indigo-600",
        outline:
          "ui-z-1 ui-relative ui-bg-white shadow-surface-outline ui-text-zinc-600 \
          before:ui-absolute before:ui-border-1 before:ui-border-zinc-200 before:ui-w-full before:ui-h-full before:ui-z-[0] \
          hover:before:ui-border-zinc-300 hover:ui-bg-black/4 \
          focus-visible:ui-outline-none focus-visible:ui-ring-1 focus-visible:ui-ring-indigo-500/50 focus-visible:ui-ring-offset-1 focus-visible:ui-ring-offset-white \
          disabled:ui-opacity-40 disabled:ui-cursor-not-allowed \
          active:ui-bg-black/6 active:after:ui-bg-white active:before:ui-border-zinc-400 ui-bg-white \
          data-[active=true]:ui-bg-black/6 data-[active=true]:after:ui-bg-white data-[active=true]:before:ui-border-zinc-400 data-[active=true]:text-zinc-700",
        ghost:
          "ui-z-1 ui-relative ui-bg-transparent ui-text-zinc-500 \
          hover:ui-bg-black/6 ui-text-white \
          focus-visible:ui-outline-none focus-visible:ui-ring-1 focus-visible:ui-ring-indigo-500/50 focus-visible:ui-ring-offset-1 focus-visible:ui-ring-offset-transparent \
          disabled:ui-opacity-40 disabled:ui-cursor-not-allowed \
          active:ui-bg-black/12 active:after:ui-bg-white active:before:ui-border-zinc-400 \
          data-[active=true]:ui-bg-black/12 data-[active=true]:after:ui-bg-white data-[active=true]:before:ui-border-zinc-400",

        error:
          "ui-z-1 ui-relative ui-bg-red-700 ui-text-white \
          hover:bg-error-gradient ui-text-white  \
          focus-visible:ui-outline-none focus-visible:ui-ring-1 focus-visible:ui-ring-indigo-500/50 focus-visible:ui-ring-offset-1 focus-visible:ui-ring-offset-transparent \
          disabled:ui-opacity-40 disabled:ui-cursor-not-allowed \
          active:bg-error-active ui-text-white",
        dashed:
          "ui-z-1 ui-relative ui-bg-white ui-text-zinc-600 ui-text-text-sm-medium \
          before:ui-absolute before:ui-border-1 before:ui-border-dashed before:ui-border-zinc-300 before:ui-w-full before:ui-h-full before:ui-z-[0] \
          hover:before:ui-border-zinc-400 hover:ui-bg-black/4 \
          focus-visible:ui-outline-none focus-visible:ui-ring-1 focus-visible:ui-ring-indigo-500/50 focus-visible:ui-ring-offset-1 focus-visible:ui-ring-offset-transparent \
          disabled:ui-opacity-40 disabled:ui-cursor-not-allowed \
          active:ui-bg-black/6 active:after:ui-bg-white active:before:ui-border-zinc-400 ui-bg-white \
          data-[active=true]:ui-bg-black/6 data-[active=true]:after:ui-bg-white data-[active=true]:before:ui-border-zinc-400 ui-bg-white",
      },
      size: {
        sm: "ui-px-[6px] ui-text-text-xs-semibold ui-rounded-lg ui-py-[5px] before:ui-rounded-lg after:ui-rounded-[10px] focus-visible:after:ui-rounded-xl ",
        md: "ui-px-[10px] ui-text-text-sm-semibold ui-rounded-lg ui-py-[7px] before:ui-rounded-lg after:ui-rounded-[10px] focus-visible:after:ui-rounded-xl ",
        lg: "ui-px-3 ui-text-text-sm-semibold ui-rounded-xl ui-py-[11px] before:ui-rounded-xl after:ui-rounded-[14px] focus-visible:after:ui-rounded-[14px] ",
        xl: "ui-px-4 ui-text-text-sm-semibold ui-rounded-xl ui-py-[15px] before:ui-rounded-xl after:ui-rounded-[14px] focus-visible:after:ui-rounded-[14px] ",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "sm",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  leftIcon?: PhosphorIcon;
  rightIcon?: PhosphorIcon;
  loading?: boolean;
  /** 0–100 visual fill for dashed loading (job stage, not true load %). Omits the timed 10%→85% animation. */
  loadingProgress?: number;
  iconProps?: {
    leftIconProps?: {
      size?: NonNullable<VariantProps<typeof iconVariants>["size"]>;
      className?: string;
      iconProps?: PhosphorIconProps
    };
    rightIconProps?: {
      size?: NonNullable<VariantProps<typeof iconVariants>["size"]>;
      className?: string;
      iconProps?: PhosphorIconProps
    };
  };
}

const PRIMARY_LOADING_CLASS =
  "before:ui-absolute before:ui-left-0 before:ui-top-0 before:ui-bottom-0 before:ui-bg-white/12 before:ui-z-[1] before:ui-content-[''] before:ui-animate-[ui-button-loading-width_3s_ease-in-out_forwards] ui-bg-gradient-to-b ui-from-indigo-600/80 ui-to-indigo-600";
const DASHED_LOADING_FILL_CLASS =
  "after:ui-absolute after:ui-left-0 after:ui-top-0 after:ui-bottom-0 after:ui-bg-zinc-500/12 after:ui-z-[1] after:ui-content-[''] after:ui-pointer-events-none ui-overflow-hidden";
const DASHED_LOADING_DETERMINATE_CLASS = `${DASHED_LOADING_FILL_CLASS} after:ui-w-[var(--flo-loading-width)] after:ui-max-w-full after:ui-transition-[width] after:ui-duration-500 after:ui-ease-out`;
const DASHED_LOADING_ANIMATED_CLASS = `${DASHED_LOADING_FILL_CLASS} after:ui-w-[10%] after:ui-animate-[ui-button-loading-width_3s_ease-in-out_forwards]`;

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = "primary",
      size,
      asChild = false,
      leftIcon,
      rightIcon,
      children,
      loading = false,
      loadingProgress,
      onClick,
      iconProps,
      style,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    const getIconSize = (size: ButtonProps["size"]) => {
      if (size === "sm" || size === "md" || size === "lg") return "sm";
      if (size === "xl") return "md";
      return "sm";
    };

    const hasDeterminateProgress =
      loading && variant === "dashed" && loadingProgress != null;

    let loadingStyles = "";
    if (loading && variant === "primary") {
      loadingStyles = PRIMARY_LOADING_CLASS;
    } else if (hasDeterminateProgress) {
      loadingStyles = DASHED_LOADING_DETERMINATE_CLASS;
    } else if (loading && variant === "dashed") {
      loadingStyles = DASHED_LOADING_ANIMATED_CLASS;
    }

    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, className }),
          loadingStyles,
          loading && "ui-cursor-not-allowed",
        )}
        ref={ref}
        style={
          hasDeterminateProgress
            ? {
                ...style,
                ["--flo-loading-width" as string]: `${Math.max(0, Math.min(100, loadingProgress))}%`,
              }
            : style
        }
        onClick={(e) => {
          if (loading) {
            return;
          }
          onClick?.(e);
        }}
        {...props}
      >
        {leftIcon && (
          <Icon
            IconComponent={leftIcon}
            size={iconProps?.leftIconProps?.size || getIconSize(size)}
            className={iconProps?.leftIconProps?.className}
          />
        )}
        <span className="ui-px-1 ui-relative ui-z-[2]">{children}</span>
        {rightIcon && (
          <Icon
            IconComponent={rightIcon}
            size={iconProps?.rightIconProps?.size || getIconSize(size)}
            className={iconProps?.rightIconProps?.className}

          />
        )}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
